/**
 * The Equipment step's manufacturer/model typeahead (docs/features/permit-plan-sets.md §11): type a few letters of
 * the maker or the model ("q.tron", "iq8", "SE7600"), pick from the catalogue, the CEC lists or the company's own
 * equipment (badges say which; discontinued and placeholder items are never offered), or "Add your own…". Keyboard:
 * ↑/↓, Enter, Esc. Nothing is chosen for the installer: an empty picker says the choice is required.
 */
import { AlertTriangle, ExternalLink, FileText, Plus, Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { S } from "../api/client";
import { Badge, cx } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { ROLE_KINDS, ROLE_LABEL, moveActive, sourceBadges, specLine, type EquipmentLike, type EquipmentRole } from "../lib/permitEquipment";
import { useDebounced, useEquipmentItem, useEquipmentSearch } from "./data";
import { useFormat } from "./format";

type Item = S["PermitEquipment"];

function Highlight({ text, q }: { text: string; q: string }) {
  const words = q.trim().split(/\s+/).filter((w) => w.length > 0);
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  return (
    <>
      {text.split(re).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>))}
    </>
  );
}

export function Badges({ item }: { item: Item | EquipmentLike }) {
  const t = useT();
  return (
    <>
      {sourceBadges(item as EquipmentLike).map((b) => (
        <span key={b.label} title={t(b.title)}>
          <Badge tone={b.tone}>{t(b.label)}</Badge>
        </span>
      ))}
    </>
  );
}

export function EquipmentPicker({ role, orgId, country, value, onChange, onAddOwn, disabled, required = true }: {
  role: EquipmentRole;
  orgId: string;
  country: string | undefined;
  value: string;
  onChange: (id: string | undefined) => void;
  onAddOwn?: (q: string) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const t = useT();
  const f = useFormat();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounced = useDebounced(q, 180);
  const kinds = ROLE_KINDS[role];
  const search = useEquipmentSearch(orgId, debounced, kinds, country, open && !disabled);
  const chosen = useEquipmentItem(value || null);
  const items = useMemo(() => search.data?.items ?? [], [search.data]);
  const options = items.length + (onAddOwn ? 1 : 0);

  useEffect(() => setActive(items.length ? 0 : -1), [debounced, items.length]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !boxRef.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (index: number) => {
    if (index < items.length) {
      onChange(items[index]!.id);
      setQ("");
      setOpen(false);
    } else if (onAddOwn) {
      setOpen(false);
      onAddOwn(q);
    }
  };
  const item = value ? chosen.data : undefined;
  const label = item ? `${item.manufacturer} ${item.model}` : "";
  const missing = required && !value;

  return (
    <div className="pw-picker" ref={boxRef}>
      <div className={cx("pw-picker-input", missing && "required")}>
        <Search size={15} className="muted" />
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={t(ROLE_LABEL[role])}
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          disabled={disabled}
          value={open ? q : label}
          placeholder={value ? label || t("common.loading") : t("permit.picker.search", { role })}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              return;
            }
            if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key) && open) {
              e.preventDefault();
              setActive((a) => moveActive(a, options, e.key));
            } else if (e.key === "ArrowDown") {
              setOpen(true);
            } else if (e.key === "Enter" && open && active >= 0) {
              e.preventDefault();
              choose(active);
            }
          }}
        />
        {value && !disabled && (
          <button
            className="icon-btn"
            aria-label={t("permit.picker.clear", { role })}
            onClick={() => {
              onChange(undefined);
              setQ("");
              inputRef.current?.focus();
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open && !disabled && (
        <ul className="pw-picker-list" id={listId} role="listbox">
          {items.map((it, i) => (
            <li
              key={it.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={cx(i === active && "active")}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
            >
              <span className="pw-opt-head">
                <b>
                  <Highlight text={`${it.manufacturer} ${it.model}`} q={debounced} />
                </b>
                <Badges item={it} />
              </span>
              <span className="small muted">{specLine(t, it as EquipmentLike, f.digits)}</span>
            </li>
          ))}
          {!items.length && (
            <li className="pw-picker-empty muted small" aria-disabled>
              {search.isFetching ? t("permit.picker.searching") : debounced ? t("permit.picker.noMatch", { query: debounced }) : t("permit.picker.typeToSearch")}
            </li>
          )}
          {onAddOwn && (
            <li
              id={`${listId}-${items.length}`}
              role="option"
              aria-selected={active === items.length}
              className={cx("pw-picker-add", active === items.length && "active")}
              onMouseEnter={() => setActive(items.length)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(items.length);
              }}
            >
              <Plus size={14} /> {t("permit.picker.addOwn", { role })}
            </li>
          )}
          {search.data && search.data.total > items.length && <li className="pw-picker-empty small muted" aria-disabled>{t("permit.picker.more", { count: search.data.total - items.length })}</li>}
        </ul>
      )}
      {missing && (
        <div className="pw-required">
          <AlertTriangle size={13} /> {t("permit.picker.required")}
        </div>
      )}
      {item && (
        <div className="pw-chosen">
          <Badges item={item} />
          <span className="small muted">{specLine(t, item as EquipmentLike, f.digits)}</span>
          {item.datasheet_url && (
            <a href={item.datasheet_url} target="_blank" rel="noreferrer noopener" className="small">
              {t("permit.picker.datasheet")}{" "}
              <DirIcon>
                <ExternalLink size={12} />
              </DirIcon>
            </a>
          )}
          {item.datasheet_file_url && (
            <a href={`/api${item.datasheet_file_url.replace(/^\/api/, "")}`} target="_blank" rel="noreferrer noopener" className="small">
              <FileText size={12} /> {t("permit.picker.datasheetPdf")}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
